import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  NGTdsModule,
  TslAutocompleteModule,
  TslButtonModule,
  TslChipModule,
  TslPopoverModule,
  TslTabsModule
} from "@tesla/ng-tds";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatInputModule } from "@angular/material/input";
import { MatTabsModule } from "@angular/material/tabs";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatChipsModule } from "@angular/material/chips";

import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FlexLayoutModule } from "@angular/flex-layout";
import { ProfileRoutingModule } from "./profile-routing.module";
import { NgxInputStarRatingModule } from "@ngx-lite/input-star-rating";

import { ProfileComponent } from "./profile.component";
import { ToolEquipmentComponent } from "./components/tool-equipment/tool-equipment.component";
import { SiteComponent } from "./components/site/site.component";
import { ProfileService } from "./services/profile.service";
import { TabTemplateService } from "./services/tab-template.service";
import { PeopleComponent } from "./components/people/people.component";
import { ToolEquipmentFormComponent } from "./components/tool-equipment/tool-equipment-form/tool-equipment-form.component";
import { FileUploadComponent } from "../shared/components/file-upload/file-upload.component";
import { LocalStorageService } from "../shared/services/index.service";
import { CommonService } from "./services/common.service";
import { PeopleService } from "./services/people.service";
import { JobtitleFormDialogComponent } from "./components/people/jobtitle-form-dialog/jobtitle-form-dialog.component";
import { ExternalFormDialogComponent } from "./components/people/external-form-dialog/external-form-dialog.component";
import { ParameterFormDialogComponent } from "./components/tool-equipment/parameter-form-dialog/parameter-form-dialog.component";
import { PickupWarehouseDialogComponent } from "./components/site/pickup-warehouse-dialog/pickup-warehouse-dialog.component";
import { CertificationDialogComponent } from "./components/site/certification-dialog/certification-dialog.component";
import { DeliverableFormDialogComponent } from "./components/tool-equipment/deliverable-form-dialog/deliverable-form-dialog.component";
import { EffectsModule } from "@ngrx/effects";
import { MasterDataService } from "./services/masterdata.service";
import { SupplierStateService } from "./services/supplierstate.service";
import { StoreModule } from "@ngrx/store";
import { supplierReducers } from "./store/app.reducer";
import { ContactGridComponent } from "./components/people/contacts/contact-grid/contact-grid.component";
import { ContactFormComponent } from "./components/people/contacts/contact-grid/contact-form/contact-form.component";
import { ManageContactComponent } from "./components/people/contacts/manage-contact/manage-contact.component";
import { ContactsService } from "./services/contacts.service";
import { AddressGridComponent } from "./components/address/address-grid/address-grid.component";
import { AddressDialogComponent } from "./components/address/address-grid/address-dialog/address-dialog.component";
import { AddressComponent } from "./components/address/address.component";
import { EventService } from "./services/event.service";
import { SoftwareService } from "./services/software.service";
import { SoftwareComponent } from "./components/software/software.component";
import { SoftwareFormDialogComponent } from "./components/software/software-form-dialog/software-form-dialog.component";
import { DisplayCellComponent } from "../shared/components/display-cell/display-cell.component";
import { SharedModule } from "../shared/shared.module";
import { InternalComponent } from "./components/internal/internal.component";
import { SupplierRatingService } from "./services/supplier-rating.service";
import { RatingFormDialogComponent } from "./components/internal/rating-form-dialog/rating-form-dialog.component";
import { SiteService } from "./services/site.service";
import { ProductGridComponent } from "./components/site/product-grid/product-grid.component";
import { ProductFormDialogComponent } from "./components/site/product-form-dialog/product-form-dialog.component";
import { MaterialFormDialogComponent } from "./components/site/material-form-dialog/material-form-dialog.component";
import { DocumentGridComponent } from "./components/document/document-grid/document-grid.component";
import { MatTableModule } from "@angular/material/table";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatSortModule } from "@angular/material/sort";
import { DocumentComponent } from "./components/document/document.component";
import { SupplierDocumentService } from "./services/supplier-document.service";
import { DocumentDialogComponent } from "./components/document/document-dialog/document-dialog.component";
import { OtherDocumentComponent } from "./components/other-document/other-document.component";
import { ExternalCertificationDialogComponent } from "./components/people/certification-dialog/external-certification-dialog.component";
import { AssociatedPartsFormDialogComponent } from "./components/tool-equipment/associated-parts-form-dialog/associated-parts-form-dialog.component";
import { TabHistoryComponent } from "./components/tab-history/tab-history.component";
import { ParameterTypeDialogComponent } from "./components/tool-equipment/parameter-type-dialog/parameter-type-dialog.component";
import { LoanWheelInventoryComponent } from "./components/loan-wheel-inventory/loan-wheel-inventory.component";
import { InventoryGridComponent } from "./components/loan-wheel-inventory/inventory-grid/inventory-grid.component";
import { InventoryFormComponent } from "./components/loan-wheel-inventory/inventory-form/inventory-form.component";
import { LoanWheelInventoryService } from "./services/loaner-wheel-inventory.service";
import { LoanerWheelInventoryEffects } from "./store/effects/loaner-wheel-inventory.effects";
import { InventoryHistoryComponent } from "./components/loan-wheel-inventory/inventory-history/inventory-history.component";
import { ManageDefaultsComponent } from "./components/tool-equipment/manage-defaults/manage-defaults.component";
import { DefaultsApprovalGridComponent } from "./components/tool-equipment/defaults-approval-grid/defaults-approval-grid.component";
import { BankComponent } from "./components/bank/bank.component";
import { BankGridComponent } from "./components/bank/bank-grid/bank-grid.component";
import { BankDialogComponent } from "./components/bank/bank-grid/bank-dialog/bank-dialog.component";
import { SecurityCheckComponent } from "./components/bank/bank-grid/security-check/security-check.component";
import { OtpGuard } from "../shared/services/otp/otp.guard";
import { BankOtpConfirmationComponent } from "./components/bank/bank-grid/bank-otp-confirmation/bank-otp-confirmation.component";
import { EmailDomainComponent } from "./components/people/email-domain/email-domain.component";
import { EmailDomainFormComponent } from "./components/people/email-domain/email-domain-form/email-domain-form.component";
import { SupplierCommonModule } from "../common/supplier-common.module";
import { TaxService } from "./services/tax.service";
import { OnboardingService } from "./services/onboarding.service";
import { ESignService } from "./services/esign.service";
import { OnboardingDocumentsService } from "./services/onboarding-document.service";
import { DetailsComponent } from "./details/details.component";
import { SidenavComponent } from "./sidenav/sidenav.component";
import { BankPopoverKatanakaContent } from "./components/bank/bank-grid/bank-dialog/bank-popover-katanaka-content.component";
import { SurveyComponent } from "./components/onboarding/survey/survey.component";
import { OnboardingTrainingComponent } from "./components/onboarding/onboarding-training/onboarding-training.component";
import { BannerComponent } from "./components/banner/banner.component";
import { AssetMismatchComponent } from "./components/tool-equipment/tool-equipment-form/asset-mismatch/asset-mismatch.component";
import { PayoutAngularModule } from "@tesla/payout-angular";
import { AppEffects } from "./store/app.effects";
import { BankService } from "./services/bank.service";
import { TranslocoModule } from "@ngneat/transloco";
import { DiversityDialogComponent } from './components/diversity/diversity-dialog/diversity-dialog.component';
import { DiversityComponent } from './components/diversity/diversity.component';
import { AddressLinkDialogComponent } from "./components/address/address-grid/address-link-dialog/address-link-dialog.component";
import { AddressDelinkDialogComponent } from './components/address/address-grid/address-delink-dialog/address-delink-dialog.component';
import { RemitToAddressComponent } from "./components/address/tabs/remitto/remitto-address.component";
import { ManufacturingAddressComponent } from "./components/address/tabs/manufacturing/manufacturing-address.component";
import { SupplierShipFromAddressComponent } from "./components/address/tabs/supplier-ship-from/supplier-ship-from-address.component";
import { TaxComponent } from './components/tax/tax.component';
import { TaxGridComponent } from './components/tax/tax-grid/tax-grid.component';
import { TaxDialogComponent } from './components/tax/tax-dialog/tax-dialog.component';
import { EhsQuestionnaireComponent } from './components/ehs-questionnaire/ehs-questionnaire.component';
import { CommentDataDialog } from "./components/tool-equipment/comment-data-dialog/comment-data-dialog.component";
import { OverrideCommentDialog } from "./components/tool-equipment/override-comment-dialog/override-comment-dialog.component";
import { PcfDialogComponent } from './components/pcf-dialog/pcf-dialog.component';
import { EinvoicingChannelsComponent } from './components/einvoicing-channels/einvoicing-channels.component';
import { EinvoicingChannelsGridComponent } from './components/einvoicing-channels/einvoicing-channels-grid/einvoicing-channels-grid.component';
import {EInvoicingService} from './services/einvoicing-channels.service';
import { ChannelOnboardingComponent } from './components/einvoicing-channels/channel-onboarding/channel-onboarding.component';
import { ReorderDeliverablesDialog } from "./components/tool-equipment/reorder-deliverables-dialog/reorder-deliverables-dialog.component";
import { CdkTableModule } from "@angular/cdk/table";
import { DragDropModule } from '@angular/cdk/drag-drop';
import { EdiStatusComponent } from './components/edi-status/edi-status.component';
import { EDIStatusService } from "./services/edi-status.service";
import { OnboardingHomeComponent } from './components/task/onboarding-home/onboarding-home.component';
import { TaskComponent } from './components/task/task.component';
import { AddressTaskWrapperComponent } from './components/task/address-task-wrapper/address-task-wrapper.component';
import { ContactTaskWrapperComponent } from './components/task/contact-task-wrapper/contact-task-wrapper.component';
import { TaskSidePanelComponent } from "./components/task/task-side-panel/task-side-panel.component";
import { BankTaskWrapperComponent } from './components/task/bank-task-wrapper/bank-task-wrapper.component';
import { TaxTaskWrapperComponent } from './components/task/tax-task-wrapper/tax-task-wrapper.component';
import { DiversityTaskWrapperComponent } from './components/task/diversity-task-wrapper/diversity-task-wrapper.component';
import { RejectionDetailsComponent } from './components/task/rejection-details/rejection-details.component';
import { ExistSelectComponent } from './components/task/exist-select/exist-select.component';
import { AddressTaskDialogComponent } from './components/task/address-task-wrapper/address-task-dialog/address-task-dialog.component';
import { TaxTaskDialogComponent } from './components/task/tax-task-wrapper/tax-task-dialog/tax-task-dialog.component';
import { BankTaskDialogComponent } from './components/task/bank-task-wrapper/bank-task-dialog/bank-task-dialog.component';
import { HomePageGuard } from "./guard/home-page.guard";
import { InsuranceTaskWrapperComponent } from './components/task/insurance-task-wrapper/insurance-task-wrapper.component';
import { NameChangeFormComponent } from './components/request-form/name-change-form/name-change-form.component';
import { DetailTaskWrapperComponent } from './components/task/detail-task-wrapper/detail-task-wrapper.component';
import { DomainTaskWrapperComponent } from './components/task/domain-task-wrapper/domain-task-wrapper.component';
import { DomainTaskDialogComponent } from "./components/task/domain-task-wrapper/domain-task-dialog/domain-task-dialog.component";
import { EntityMappingComponent } from './components/task/entity-mapping/entity-mapping.component';
import { RequestListComponent } from './components/request-list/request-list.component';
import { CancelRequestDialogComponent } from './components/request-list/cancel-request-dialog/cancel-request-dialog.component';
import { MfaBannerComponent } from './components/mfa-banner/mfa-banner.component';
import { MsmeTaskWrapperComponent } from './components/task/msme-task-wrapper/msme-task-wrapper.component';


@NgModule({
  declarations: [
    ProfileComponent,
    ToolEquipmentComponent,
    ToolEquipmentFormComponent,
    SiteComponent,
    PeopleComponent,
    FileUploadComponent,
    JobtitleFormDialogComponent,
    ExternalFormDialogComponent,
    PickupWarehouseDialogComponent,
    AddressDialogComponent,
    AddressLinkDialogComponent,
    CertificationDialogComponent,
    ParameterFormDialogComponent,
    DeliverableFormDialogComponent,
    ContactGridComponent,
    ContactFormComponent,
    PcfDialogComponent,
    ManageContactComponent,
    AddressGridComponent,
    AddressComponent,
    SoftwareComponent,
    SoftwareFormDialogComponent,
    InternalComponent,
    RatingFormDialogComponent,
    ProductGridComponent,
    ProductFormDialogComponent,
    MaterialFormDialogComponent,
    DocumentGridComponent,
    DocumentComponent,
    DocumentDialogComponent,
    OtherDocumentComponent,
    ExternalCertificationDialogComponent,
    AssociatedPartsFormDialogComponent,
    TabHistoryComponent,
    ParameterTypeDialogComponent,
    LoanWheelInventoryComponent,
    InventoryGridComponent,
    InventoryFormComponent,
    InventoryHistoryComponent,
    ManageDefaultsComponent,
    DefaultsApprovalGridComponent,
    BankComponent,
    BankGridComponent,
    BankDialogComponent,
    SecurityCheckComponent,
    BankOtpConfirmationComponent,
    EmailDomainComponent,
    EmailDomainFormComponent,
    DetailsComponent,
    SidenavComponent,
    BankPopoverKatanakaContent,
    BannerComponent,
    EmailDomainFormComponent,
    DetailsComponent,
    SurveyComponent,
    OnboardingTrainingComponent,
    AssetMismatchComponent,
    DiversityComponent,
    DiversityDialogComponent,
    AddressDelinkDialogComponent,
    RemitToAddressComponent,
    ManufacturingAddressComponent,
    SupplierShipFromAddressComponent,
    TaxComponent,
    TaxGridComponent,
    TaxDialogComponent,
    EhsQuestionnaireComponent,
    CommentDataDialog,
    OverrideCommentDialog,
    EinvoicingChannelsComponent,
    EinvoicingChannelsGridComponent,
    ChannelOnboardingComponent,
    ReorderDeliverablesDialog,
    EdiStatusComponent,
    OnboardingHomeComponent,
    TaskComponent,
    AddressTaskWrapperComponent,
    ContactTaskWrapperComponent,
    TaskSidePanelComponent,
    BankTaskWrapperComponent,
    TaxTaskWrapperComponent,
    DiversityTaskWrapperComponent,
    RejectionDetailsComponent,
    ExistSelectComponent,
    AddressTaskDialogComponent,
    TaxTaskDialogComponent,
    BankTaskDialogComponent,
    InsuranceTaskWrapperComponent,
    NameChangeFormComponent,
    DetailTaskWrapperComponent,
    DomainTaskWrapperComponent,
    DomainTaskDialogComponent,
    EntityMappingComponent,
    RequestListComponent,
    CancelRequestDialogComponent,
    MfaBannerComponent,
    MsmeTaskWrapperComponent,
  ],
  providers: [
    LocalStorageService,
    ProfileService,
    TabTemplateService,
    CommonService,
    PeopleService,
    SupplierStateService,
    EventService,
    MasterDataService,
    ContactsService,
    SoftwareService,
    SupplierRatingService,
    SiteService,
    SupplierDocumentService,
    LoanWheelInventoryService,
    OtpGuard,
    TaxService,
    OnboardingService,
    ESignService,
    OnboardingDocumentsService,
    EInvoicingService,
    BankService,
    EDIStatusService,
    HomePageGuard,
    {
      provide: MatDialogRef,
      useValue: []
    }
  ],
  imports: [
    CommonModule,
    NGTdsModule,
    TslAutocompleteModule,
    TslChipModule,
    TslPopoverModule,
    TslTabsModule,
    TslButtonModule,
    MatExpansionModule,
    MatCardModule,
    MatFormFieldModule,
    MatTooltipModule,
    MatInputModule,
    MatTabsModule,
    MatDialogModule,
    MatDividerModule,
    MatChipsModule,
    SharedModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatSelectModule,
    MatSnackBarModule,
    NgxInputStarRatingModule,
    FlexLayoutModule,
    ProfileRoutingModule,
    EffectsModule.forFeature(AppEffects),
    StoreModule.forFeature("SupplierModule", supplierReducers),
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    SupplierCommonModule,
    PayoutAngularModule,
    TranslocoModule,
    CdkTableModule,
    DragDropModule
  ],
  entryComponents: [
    SiteComponent,
    MatDialogModule,
    PeopleComponent,
    ToolEquipmentComponent,
    ToolEquipmentFormComponent,
    JobtitleFormDialogComponent,
    ExternalFormDialogComponent,
    PickupWarehouseDialogComponent,
    AddressDialogComponent,
    AddressLinkDialogComponent,
    CertificationDialogComponent,
    ParameterFormDialogComponent,
    DeliverableFormDialogComponent,
    BankOtpConfirmationComponent,
    PcfDialogComponent,
    MfaBannerComponent,
    ContactFormComponent,
    AddressComponent,
    SoftwareComponent,
    SoftwareFormDialogComponent,
    DisplayCellComponent,
    InternalComponent,
    RatingFormDialogComponent,
    ProductFormDialogComponent,
    MaterialFormDialogComponent,
    DocumentGridComponent,
    DocumentComponent,
    DocumentDialogComponent,
    OtherDocumentComponent,
    ExternalCertificationDialogComponent,
    AssociatedPartsFormDialogComponent,
    TabHistoryComponent,
    ParameterTypeDialogComponent,
    LoanWheelInventoryComponent,
    InventoryFormComponent,
    InventoryHistoryComponent,
    ManageDefaultsComponent,
    BankComponent,
    BankDialogComponent,
    SecurityCheckComponent,
    EmailDomainFormComponent,
    BankPopoverKatanakaContent,
    BannerComponent,
    AssetMismatchComponent,
    DiversityDialogComponent,
    EhsQuestionnaireComponent,
    AddressTaskWrapperComponent
  ],
  exports: [ContactGridComponent, AddressGridComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProfileModule {}
